import { ArrowLeft, ArrowRight, Columns3, Download, Eye, EyeOff, FileSpreadsheet } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  columnsFor,
  moveColumn,
  moveColumnTo,
  pickerRows,
  tableGrid,
  toCsv,
  toggleColumn,
  EMPTY_TABLE_VIEW,
  type ColumnDef,
} from '../lib/table';
import { cn } from '../lib/utils';
import { useAppStore } from '../store/useAppStore';
import { Button, EmptyState, IconButton } from './ui';

/**
 * A table of anything, with the columns somebody chose, in the order they put
 * them, and a way to get it out of the app.
 *
 * The rows and the columns come from the caller; everything about *arranging*
 * them is here, so the people table and the changes table cannot drift apart on
 * the part that is the same. `table` is the key the arrangement is stored under
 * in the browser — change it and everybody's columns reset, so don't.
 */
export function DataTable<Row>({
  table,
  columns,
  rows,
  rowKey,
  rowTone,
  onRowClick,
  onRowDoubleClick,
  filename,
  empty,
  toolbar,
}: {
  table: string;
  columns: readonly ColumnDef<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  /** A wash behind the row, for whatever the caller is colouring by. */
  rowTone?: (row: Row) => string | null;
  onRowClick?: (row: Row) => void;
  /** For the thing a row can do that is more than opening it. */
  onRowDoubleClick?: (row: Row) => void;
  /** Basename for a downloaded file, without the extension. */
  filename: string;
  empty: { title: string; hint: string };
  /** Anything else that belongs on the toolbar, to the left of the buttons. */
  toolbar?: ReactNode;
}) {
  const view = useAppStore((s) => s.tableViews[table]) ?? EMPTY_TABLE_VIEW;
  const setTableView = useAppStore((s) => s.setTableView);
  const sheetsAvailable = useAppStore((s) => s.sheetsAvailable);
  const exportSheet = useAppStore((s) => s.exportSheet);

  const [picking, setPicking] = useState(false);

  /*
   * Which column is being dragged, and which one it is over.
   *
   * Dragging a header is the way people expect to rearrange a table, so it is
   * here — but it needs a pointer and a steady hand, so the arrows in the picker
   * stay as the way to do it from a keyboard or a phone.
   */
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);

  const endDrag = () => {
    setDragging(null);
    setOver(null);
  };

  /*
   * Which covered figures are showing: a set of row keys, or every one of
   * them.
   *
   * Not persisted and not in the store — it is a decision about this minute in
   * front of this screen, and one that should not survive switching scenarios
   * or come back tomorrow having been forgotten about.
   */
  const [revealed, setRevealed] = useState<Set<string> | 'all'>(new Set());
  /** The row whose figure was just clicked, while the question is on screen. */
  const [asking, setAsking] = useState<string | null>(null);

  const isRevealed = (key: string) => revealed === 'all' || revealed.has(key);

  const revealOne = (key: string) => {
    setRevealed((current) => (current === 'all' ? current : new Set(current).add(key)));
    setAsking(null);
  };

  const cover = (key: string) => {
    setAsking(null);
    setRevealed((current) => {
      if (current === 'all') return new Set<string>();
      const next = new Set(current);
      next.delete(key);
      return next;
    });
  };

  const shown = columnsFor(columns, view);
  const grid = () => tableGrid(shown, rows);

  const downloadCsv = () => {
    /*
     * A BOM, because the audience for this file opens it in Excel, and Excel
     * reads a UTF-8 CSV as Latin-1 without one — which turns every name with an
     * accent in it into mojibake before anybody has done anything wrong.
     */
    const blob = new Blob([`\uFEFF${toCsv(grid())}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--color-line)] px-3 py-2">
        <span className="text-[12px] text-[var(--color-ink-2)] tabular-nums">
          {rows.length} {rows.length === 1 ? 'row' : 'rows'}
        </span>

        {/*
          Asked here rather than beside the figure that was clicked. The table
          body scrolls, so anything absolutely positioned inside a cell is
          clipped by it — and the money column is usually the rightmost one,
          where there is least room for a popup to open into.
        */}
        {asking !== null ? (
          <div
            role="group"
            aria-label="Show amounts"
            className={cn(
              'flex items-center gap-1.5 rounded-lg border border-[var(--color-brand)]/40',
              'bg-[var(--color-brand)]/5 py-0.5 pr-0.5 pl-2',
            )}
          >
            <span className="text-[12px] text-[var(--color-ink-2)]">Show amounts?</span>
            <Button size="sm" variant="ghost" onClick={() => revealOne(asking)}>
              Just this one
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setRevealed('all');
                setAsking(null);
              }}
            >
              Show all
            </Button>
          </div>
        ) : null}

        {toolbar}

        <div className="ml-auto flex items-center gap-1.5">
          <div className="relative">
            <Button variant="outline" size="sm" onClick={() => setPicking(!picking)}>
              <Columns3 size={13} />
              Columns
            </Button>

            {picking ? (
              <>
                {/*
                  A plain backdrop rather than a focus trap: this is a list of
                  checkboxes, and clicking the thing behind it is a reasonable
                  way to say you are finished with it.
                */}
                <button
                  type="button"
                  aria-label="Close the column picker"
                  className="fixed inset-0 z-30 cursor-default"
                  onClick={() => setPicking(false)}
                />
                <div
                  role="group"
                  aria-label="Columns"
                  className={cn(
                    'absolute right-0 z-40 mt-1 max-h-80 w-64 overflow-y-auto rounded-lg border',
                    'border-[var(--color-line)] bg-[var(--color-surface)] p-1 shadow-[var(--shadow-panel)]',
                  )}
                >
                  {pickerRows(columns, view).map(({ def, shown: on }, index, all) => (
                    <div key={def.key} className="flex items-center gap-1 pr-1">
                      <label className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-[12.5px] text-[var(--color-ink)] hover:bg-[var(--color-surface-2)]">
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => setTableView(table, toggleColumn(columns, view, def.key))}
                          className="size-3.5 accent-[var(--color-brand-fill)]"
                        />
                        <span className="truncate">{def.label}</span>
                      </label>
                      {/*
                        Arrows rather than dragging. A drag needs a pointer and a
                        steady hand; these work from the keyboard, read out
                        properly, and are the same two clicks on a phone.
                      */}
                      {on ? (
                        <>
                          <IconButton
                            label={`Move ${def.label} left`}
                            className="size-6"
                            disabled={index === 0}
                            onClick={() => setTableView(table, moveColumn(columns, view, def.key, -1))}
                          >
                            <ArrowLeft size={11} />
                          </IconButton>
                          <IconButton
                            label={`Move ${def.label} right`}
                            className="size-6"
                            disabled={!all[index + 1]?.shown}
                            onClick={() => setTableView(table, moveColumn(columns, view, def.key, 1))}
                          >
                            <ArrowRight size={11} />
                          </IconButton>
                        </>
                      ) : null}
                    </div>
                  ))}
                </div>
              </>
            ) : null}
          </div>

          <Button variant="outline" size="sm" onClick={downloadCsv} disabled={rows.length === 0}>
            <Download size={13} />
            CSV
          </Button>

          {/*
            Only offered when the app has a Google account to create the file
            with. Locally there is none, and a button that always errors is worse
            than one that is not there.
          */}
          {sheetsAvailable ? (
            <Button
              variant="outline"
              size="sm"
              disabled={rows.length === 0}
              onClick={() => void exportSheet(filename, grid())}
            >
              <FileSpreadsheet size={13} />
              Google Sheet
            </Button>
          ) : null}
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={<Columns3 size={18} />} title={empty.title} hint={empty.hint} />
      ) : (
        /*
         * Scrolls in both directions inside its own box, with the header stuck
         * to the top. A table of three hundred people is the normal case, and
         * the page itself must not be what grows.
         */
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="w-full border-collapse text-[12.5px]">
            <thead className="sticky top-0 z-10">
              <tr>
                {shown.map((def) => (
                  <th
                    key={def.key}
                    scope="col"
                    draggable
                    onDragStart={(event) => {
                      setDragging(def.key);
                      // Firefox starts no drag at all without something to carry.
                      event.dataTransfer.setData('text/plain', def.key);
                      event.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragOver={(event) => {
                      // Preventing the default is what makes this a drop target.
                      event.preventDefault();
                      event.dataTransfer.dropEffect = 'move';
                      if (over !== def.key) setOver(def.key);
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      const key = dragging ?? event.dataTransfer.getData('text/plain');
                      if (key) setTableView(table, moveColumnTo(columns, view, key, def.key));
                      endDrag();
                    }}
                    onDragEnd={endDrag}
                    className={cn(
                      'border-b border-[var(--color-line)] bg-[var(--color-surface)] px-3 py-2',
                      'text-left text-[10.5px] font-semibold tracking-wide text-[var(--color-ink-3)] uppercase',
                      'cursor-grab whitespace-nowrap select-none active:cursor-grabbing',
                      def.align === 'right' && 'text-right',
                      dragging === def.key && 'opacity-50',
                      // Where it would land, drawn on the edge it would arrive at.
                      over === def.key &&
                        dragging !== null &&
                        dragging !== def.key &&
                        'shadow-[inset_2px_0_0_0_var(--color-brand)]',
                    )}
                    title={`${def.label} — drag to reorder`}
                  >
                    {def.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const tone = rowTone?.(row) ?? null;
                return (
                  <tr
                    key={rowKey(row)}
                    style={tone ? { background: tone } : undefined}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    onDoubleClick={onRowDoubleClick ? () => onRowDoubleClick(row) : undefined}
                    className={cn(
                      'border-b border-[var(--color-line)]',
                      onRowClick && 'cursor-pointer hover:bg-[var(--color-surface-2)]',
                    )}
                  >
                    {shown.map((def) => {
                      const value = def.value(row);
                      const key = rowKey(row);
                      const covered = def.secret === true && value !== null && !isRevealed(key);
                      return (
                        <td
                          key={def.key}
                          className={cn(
                            'max-w-[22rem] truncate px-3 py-1.5 text-[var(--color-ink)]',
                            def.align === 'right' && 'text-right tabular-nums',
                          )}
                          title={covered || value === null ? undefined : String(value)}
                        >
                          {def.secret === true && value !== null ? (
                            <SecretCell
                              value={String(value)}
                              covered={covered}
                              onReveal={() => {
                                /*
                                 * The first one asks; after that the answer is
                                 * known. Asking every time would make covering
                                 * the figures the thing in the way rather than
                                 * the thing protecting them.
                                 */
                                if (revealed === 'all' || revealed.size > 0) revealOne(key);
                                else setAsking(key);
                              }}
                              onCover={() => cover(key)}
                            />
                          ) : (
                            value
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * One covered figure.
 *
 * The same cover as `SecretAmount` on a card — ten dots whatever the number is,
 * because a mask that grew with the figure would leak it. The question about
 * one or all is asked in the toolbar rather than here; see there for why.
 */
function SecretCell({
  value,
  covered,
  onReveal,
  onCover,
}: {
  value: string;
  covered: boolean;
  onReveal: () => void;
  onCover: () => void;
}) {
  return (
    <span className="inline-flex items-center justify-end gap-1.5">
      {covered ? (
        <span aria-hidden className="leading-none tracking-tight">
          ●●●●●●●●●●
        </span>
      ) : (
        <span>{value}</span>
      )}
      <button
        type="button"
        aria-label={covered ? 'Show the amount' : 'Hide the amount'}
        onClick={(event) => {
          // The row underneath opens somebody; uncovering a figure is not that.
          event.stopPropagation();
          if (covered) onReveal();
          else onCover();
        }}
        className="shrink-0 text-[var(--color-ink-3)] transition-colors hover:text-[var(--color-ink)]"
      >
        {covered ? <Eye size={13} /> : <EyeOff size={13} />}
      </button>
    </span>
  );
}
